import React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import PhoneIcon from "@mui/icons-material/Phone";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BuildIcon from "@mui/icons-material/Build";
import TimelineIcon from "@mui/icons-material/Timeline";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SpeedIcon from "@mui/icons-material/Speed";
import { tokens } from "../../theme";

export default function PremiumFeature() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const features = [
    {
      icon: <BuildIcon />,
      title: "Advanced Maintenance",
      description:
        "Comprehensive fleet maintenance tracking with automated scheduling and alerts",
    },
    {
      icon: <TimelineIcon />,
      title: "Analytics Dashboard",
      description: "Detailed performance metrics and cost analysis tools",
    },
    {
      icon: <SpeedIcon />,
      title: "Performance Tracking",
      description: "Real-time monitoring and efficiency reports for your fleet",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: colors.primary[400],
        p: { xs: 2, sm: 4, md: 6 },
        borderRadius: "12px",
        minHeight: "80vh",
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <StarIcon
            sx={{
              fontSize: "60px",
              color: colors.greenAccent[500],
              filter: "drop-shadow(0px 0px 8px rgba(76, 206, 172, 0.3))",
            }}
          />
        </Box>
        <Typography
          variant="h2"
          color={colors.grey[100]}
          fontWeight="bold"
          sx={{ mb: 2 }}
        >
          Premium Features Await
        </Typography>
        <Typography
          variant="h5"
          color={colors.greenAccent[400]}
          sx={{ mb: 4, maxWidth: "600px", mx: "auto" }}
        >
          Unlock advanced fleet management capabilities with our Pro
          subscription
        </Typography>
      </Box>

      {/* Features Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: 4,
          mb: 6,
        }}
      >
        {features.map((feature, index) => (
          <Box
            key={index}
            sx={{
              backgroundColor: colors.primary[500],
              p: 3,
              borderRadius: "8px",
              transition: "all 0.3s ease",
              border: `1px solid ${colors.primary[600]}`,
              "&:hover": {
                backgroundColor: colors.primary[600],
                transform: "translateY(-4px)",
                boxShadow: `0 4px 20px 0 ${colors.primary[700]}`,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  color: colors.greenAccent[500],
                  fontSize: "40px",
                  mb: 2,
                }}
              >
                {feature.icon}
              </Box>
              <Typography
                variant="h4"
                color={colors.grey[100]}
                fontWeight="bold"
                sx={{ mb: 2 }}
              >
                {feature.title}
              </Typography>
              <Typography color={colors.grey[200]}>
                {feature.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* CTA Section */}
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "center",
            mb: 4,
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.primary[500],
              fontSize: "16px",
              fontWeight: "bold",
              py: 2,
              px: 4,
              "&:hover": {
                backgroundColor: colors.greenAccent[500],
              },
            }}
            endIcon={<ChevronRightIcon />}
          >
            Upgrade to Pro
          </Button>
          <Button
            variant="outlined"
            sx={{
              color: colors.grey[100],
              borderColor: colors.grey[400],
              fontSize: "16px",
              fontWeight: "bold",
              py: 2,
              px: 4,
              "&:hover": {
                borderColor: colors.grey[300],
                backgroundColor: "transparent",
              },
            }}
            startIcon={<PhoneIcon />}
          >
            Contact Sales
          </Button>
        </Box>

        {/* Trust Indicators */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            flexWrap: "wrap",
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: colors.grey[200],
            }}
          >
            <SecurityIcon sx={{ color: colors.greenAccent[500] }} />
            <Typography>Secure Payment</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: colors.grey[200],
            }}
          >
            <SupportAgentIcon sx={{ color: colors.greenAccent[500] }} />
            <Typography>24/7 Support</Typography>
          </Box>
        </Box>

        {/* Footer Text */}
        {/* <Typography color={colors.grey[300]}>
          Already a Pro member?{' '}
          <Box
            component="span"
            sx={{
              color: colors.greenAccent[500],
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                color: colors.greenAccent[400]
              }
            }}
          >
            Sign in
          </Box>
        </Typography> */}
      </Box>
    </Box>
  );
}
